import type {
  KnowledgeBaseTreeNode,
  KnowledgeBaseTreeNodeType,
} from "models/knowledge-base/knowledge-base-tree.types";

export type DropEdge = "top" | "bottom";

export interface NodeContext {
  node: KnowledgeBaseTreeNode;
  // The array the node lives in (the working tree for top-level nodes).
  parentList: KnowledgeBaseTreeNode[];
  index: number;
  // Depth of `parentList`: top-level = 0, a top category's children = 1,
  // a subcategory's children = 2.
  level: number;
}

// Locate a node by its client uid, returning the list it lives in, its index
// in that list, and the level of that list. Searches the whole tree.
export const findNodeContext = (
  tree: KnowledgeBaseTreeNode[],
  uid: string,
  level = 0,
): NodeContext | null => {
  for (let index = 0; index < tree.length; index++) {
    const node = tree[index];
    if (node.uid === uid) {
      return { node, parentList: tree, index, level };
    }
    if (node.type === "category") {
      const found = findNodeContext(node.children, uid, level + 1);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

// True when `uid` lives anywhere inside `node`'s subtree.
const containsUid = (node: KnowledgeBaseTreeNode, uid: string): boolean => {
  if (node.type !== "category") {
    return false;
  }
  return node.children.some(
    (child) => child.uid === uid || containsUid(child, uid),
  );
};

const hasCategoryChild = (node: KnowledgeBaseTreeNode): boolean =>
  node.type === "category" &&
  node.children.some((child) => child.type === "category");

// Whether a node described by (type, hasCategoryChild) may live in a list at
// `destLevel`. Encodes the 2-level depth invariant:
//   - items live only inside (sub)categories (level 1 or 2), never at the root;
//   - categories live at the root (0) or as a subcategory (1), never deeper;
//   - a category that itself owns sub-categories may not become a subcategory.
export const canPlaceByDescriptor = (
  type: KnowledgeBaseTreeNodeType,
  ownsCategory: boolean,
  destLevel: number,
): boolean => {
  if (type === "item") {
    return destLevel === 1 || destLevel === 2;
  }
  if (destLevel === 0) {
    return true;
  }
  if (destLevel === 1) {
    return ownsCategory === false;
  }
  return false;
};

const canPlaceNode = (
  source: KnowledgeBaseTreeNode,
  destLevel: number,
): boolean =>
  canPlaceByDescriptor(source.type, hasCategoryChild(source), destLevel);

// Move `sourceUid` to sit next to `targetUid` (above or below it) in the
// target's list. Mutates `tree` in place. Returns false when the move is
// blocked (depth rule, self/own-subtree, unknown uid) and leaves the tree
// untouched.
export const moveNodeRelativeTo = (
  tree: KnowledgeBaseTreeNode[],
  sourceUid: string,
  targetUid: string,
  edge: DropEdge,
): boolean => {
  if (sourceUid === targetUid) {
    return false;
  }
  const source = findNodeContext(tree, sourceUid);
  const target = findNodeContext(tree, targetUid);
  if (source === null || target === null) {
    return false;
  }
  if (containsUid(source.node, targetUid)) {
    return false;
  }
  if (canPlaceNode(source.node, target.level) === false) {
    return false;
  }

  source.parentList.splice(source.index, 1);
  const targetIndex = target.parentList.indexOf(target.node);
  const insertAt = edge === "top" ? targetIndex : targetIndex + 1;
  target.parentList.splice(insertAt, 0, source.node);
  return true;
};

// Append `sourceUid` to the end of a container's children. `containerUid` of
// null targets the top level. Mutates `tree` in place; returns false when
// blocked or the container is missing/not a category.
export const moveNodeIntoContainer = (
  tree: KnowledgeBaseTreeNode[],
  sourceUid: string,
  containerUid: string | null,
): boolean => {
  const source = findNodeContext(tree, sourceUid);
  if (source === null) {
    return false;
  }

  let destList: KnowledgeBaseTreeNode[];
  let destLevel: number;

  if (containerUid === null) {
    destList = tree;
    destLevel = 0;
  } else {
    if (containerUid === sourceUid || containsUid(source.node, containerUid)) {
      return false;
    }
    const container = findNodeContext(tree, containerUid);
    if (container === null || container.node.type !== "category") {
      return false;
    }
    destList = container.node.children;
    destLevel = container.level + 1;
  }

  if (canPlaceNode(source.node, destLevel) === false) {
    return false;
  }

  source.parentList.splice(source.index, 1);
  destList.push(source.node);
  return true;
};
